import React, { useEffect, useState } from "react";
import { AjustesBuzon, FiltroCorreo, ajustesApi, errorCorreo } from "./wappmail.service";

type Pestana = "fuera" | "reenvio" | "filtros";
const CARPETAS_BASE = ["Archive", "Junk", "Trash"];
const DOMINIO = "@washingtonacademy.com";
const hoy = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };

/**
 * Ajustes del buzón: Fuera de Oficina, Reenvío y Filtros. Sin `buzonId` es el mío; con `buzonId` es el de otra persona (Super Admin, desde Buzones:
 * por ejemplo cuando alguien se va y su correo pasa a su jefe). Lo guarda WAPP (Migadu + un script Sieve que maneja wa-mail).
 */
const AjustesCorreo: React.FC<{ buzonId?: number; titulo?: string; carpetas?: string[]; esSuper?: boolean; alCerrar: () => void }> = ({ buzonId, titulo, carpetas, esSuper, alCerrar }) => {
	const [a, setA] = useState<AjustesBuzon | null>(null);
	const [pestana, setPestana] = useState<Pestana>("fuera");
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState(false);
	const [forzar, setForzar] = useState(false);
	const [fuera, setFuera] = useState<any>({ activo: false, asunto: "Fuera de oficina", cuerpo: "", desde: "", hasta: "" });
	const [reenvio, setReenvio] = useState<any>({ activo: false, a: "", copia: true, hasta: "" });
	const [filtros, setFiltros] = useState<FiltroCorreo[]>([]);

	const cargar = (x: AjustesBuzon) => {
		setA(x);
		setFuera({ activo: !!x.fuera.activo, asunto: x.fuera.asunto || "Fuera de oficina", cuerpo: x.fuera.cuerpo || "", desde: x.fuera.desde || "", hasta: x.fuera.hasta || "" });
		setReenvio({ activo: !!x.reenvio.activo, a: x.reenvio.a || "", copia: x.reenvio.copia !== false, hasta: x.reenvio.hasta || "" });
		setFiltros(x.filtros || []);
	};
	useEffect(() => {
		let vivo = true;
		ajustesApi.leer(buzonId).then((r) => { if (vivo) cargar(r.data); }).catch((e) => { if (vivo) setError(errorCorreo(e)); });
		return () => { vivo = false; };
	}, [buzonId]);

	const guardar = async (parte: any, mensaje: string) => {
		setOcupado(true); setError(null); setOk(null);
		try {
			cargar((await ajustesApi.guardar({ ...parte, forzar: esSuper && forzar ? true : undefined }, buzonId)).data);
			setOk(mensaje);
		} catch (e) { setError(errorCorreo(e)); } finally { setOcupado(false); }
	};

	const guardarFuera = () => guardar({ fuera: fuera.activo ? { activo: true, asunto: fuera.asunto, cuerpo: fuera.cuerpo, desde: fuera.desde || null, hasta: fuera.hasta || null } : { activo: false } },
		fuera.activo ? "Fuera de Oficina activado." : "Fuera de Oficina apagado.");
	const guardarReenvio = () => guardar({ reenvio: reenvio.activo ? { activo: true, a: reenvio.a, copia: reenvio.copia, hasta: reenvio.hasta || null } : { activo: false } },
		reenvio.activo ? "Reenvío guardado." : "Reenvío apagado.");
	const guardarFiltros = () => guardar({ filtros }, "Filtros guardados.");

	const esInterno = String(reenvio.a || "").toLowerCase().endsWith(DOMINIO);
	const bloqueoSieve = !!a && (!!a.ocupado_por && !(esSuper && forzar));
	const sinSieve = !!a?.sieve_error;
	const lista = Array.from(new Set([...(carpetas || []).filter((c) => c !== "INBOX"), ...CARPETAS_BASE]));
	const ponerFiltro = (i: number, cambio: Partial<FiltroCorreo>) => setFiltros(filtros.map((f, j) => (j === i ? { ...f, ...cambio } : f)));

	return (
		<div className="aj-fondo" onClick={alCerrar}>
			<div className="aj-hoja" role="dialog" aria-label="Ajustes del correo" onClick={(e) => e.stopPropagation()}>
				<div className="aj-cab">
					<div><h2>Ajustes del Correo</h2><span className="av-tenue">{titulo || a?.email || ""}</span></div>
					<button className="av-x grande" onClick={alCerrar} aria-label="Cerrar">×</button>
				</div>
				<div className="aj-tabs" role="tablist">
					{([["fuera", "Fuera de Oficina"], ["reenvio", "Reenvío"], ["filtros", "Filtros"]] as [Pestana, string][]).map(([k, t]) => (
						<button key={k} role="tab" aria-selected={pestana === k} className={pestana === k ? "on" : ""} onClick={() => { setPestana(k); setOk(null); setError(null); }}>{t}</button>
					))}
				</div>
				{error ? <div className="wm-aviso oro" role="alert">{error}</div> : null}
				{ok ? <div className="wm-aviso ok" role="status">{ok}</div> : null}
				{!a && !error ? <div className="av-vacio">Cargando…</div> : null}
				{a && a.ocupado_por && pestana !== "reenvio" ? (
					<div className="wm-aviso oro">Este buzón ya tiene un filtro activo hecho en otra aplicación («{a.ocupado_por}»). WappMail no lo reemplaza.
						{esSuper ? <label className="av-check aj-forzar"><input type="checkbox" checked={forzar} onChange={(e) => setForzar(e.target.checked)} /> Reemplazarlo de todos modos</label> : null}</div>
				) : null}
				{a && sinSieve && pestana !== "reenvio" ? <div className="wm-aviso oro">{a.sieve_error}</div> : null}

				{a && pestana === "fuera" ? (
					<div className="aj-cuerpo">
						<label className="av-check"><input type="checkbox" checked={fuera.activo} onChange={(e) => setFuera({ ...fuera, activo: e.target.checked })} /> Activar Respuesta Automática</label>
						<label className="aj-campo">Asunto<input className="av-buscar" maxLength={200} value={fuera.asunto} disabled={!fuera.activo} onChange={(e) => setFuera({ ...fuera, asunto: e.target.value })} /></label>
						<label className="aj-campo">Mensaje<textarea className="av-buscar aj-texto" rows={6} maxLength={4000} value={fuera.cuerpo} disabled={!fuera.activo}
							placeholder="Estaré fuera de la oficina. Si es urgente, escribe a…" onChange={(e) => setFuera({ ...fuera, cuerpo: e.target.value })} /></label>
						<div className="aj-fechas">
							<label className="aj-campo">Desde (opcional)<input type="date" className="av-buscar" value={fuera.desde} disabled={!fuera.activo} onChange={(e) => setFuera({ ...fuera, desde: e.target.value })} /></label>
							<label className="aj-campo">Hasta, Inclusive (opcional)<input type="date" className="av-buscar" min={fuera.desde || hoy()} value={fuera.hasta} disabled={!fuera.activo} onChange={(e) => setFuera({ ...fuera, hasta: e.target.value })} /></label>
						</div>
						<span className="av-tenue">Contesta una vez al día a cada persona y no a listas ni boletines. Sin fecha final, sigue hasta que lo apagues.</span>
						<div className="aj-pie"><button className="av-btn pri" disabled={ocupado || bloqueoSieve || sinSieve || (fuera.activo && (!fuera.asunto.trim() || !fuera.cuerpo.trim()))} onClick={guardarFuera}>Guardar Cambios</button></div>
					</div>
				) : null}

				{a && pestana === "reenvio" ? (
					<div className="aj-cuerpo">
						<label className="av-check"><input type="checkbox" checked={reenvio.activo} onChange={(e) => setReenvio({ ...reenvio, activo: e.target.checked })} /> Reenviar Mi Correo</label>
						<label className="aj-campo">Reenviar a<input type="email" className="av-buscar" maxLength={200} placeholder="nombre@washingtonacademy.com o un correo externo" value={reenvio.a} disabled={!reenvio.activo}
							onChange={(e) => setReenvio({ ...reenvio, a: e.target.value })} /></label>
						<label className="av-check"><input type="checkbox" checked={esInterno ? !!reenvio.copia : true} disabled={!reenvio.activo || !esInterno}
							onChange={(e) => setReenvio({ ...reenvio, copia: e.target.checked })} /> Conservar una copia en este buzón</label>
						<label className="aj-campo aj-corto">Hasta, Inclusive (opcional)<input type="date" className="av-buscar" min={hoy()} value={reenvio.hasta} disabled={!reenvio.activo} onChange={(e) => setReenvio({ ...reenvio, hasta: e.target.value })} /></label>
						<span className="av-tenue">
							{reenvio.activo && reenvio.a && !esInterno
								? "A un correo de fuera, Migadu le pide confirmación al destinatario y siempre deja copia aquí."
								: "A una dirección de Washington Academy se reenvía al instante; puedes quitar la copia."}
						</span>
						{a.reenvio_externo && a.reenvio.activo && !a.reenvio_externo.confirmado ? <div className="wm-aviso suave">Esperando que {a.reenvio.a} confirme el reenvío. Hasta entonces no se reenvía nada.</div> : null}
						{a.reenvio_externo?.bloqueado ? <div className="wm-aviso oro">Migadu bloqueó este reenvío. Prueba con otra dirección.</div> : null}
						<div className="aj-pie"><button className="av-btn pri" disabled={ocupado || (reenvio.activo && !reenvio.a.trim()) || (esInterno && (bloqueoSieve || sinSieve))} onClick={guardarReenvio}>Guardar Cambios</button></div>
					</div>
				) : null}

				{a && pestana === "filtros" ? (
					<div className="aj-cuerpo">
						{!filtros.length ? <div className="av-tenue">Sin filtros. Un filtro mueve, marca o elimina el correo que llega según quién lo manda o de qué trata.</div> : null}
						{filtros.map((f, i) => (
							<div className="aj-filtro" key={i}>
								<span className="aj-si">Si</span>
								<select className="av-buscar" aria-label="Campo" value={f.campo} onChange={(e) => ponerFiltro(i, { campo: e.target.value as any })}>
									<option value="de">De</option><option value="para">Para o Cc</option><option value="asunto">Asunto</option><option value="cuerpo">Mensaje</option>
								</select>
								<select className="av-buscar" aria-label="Condición" value={f.op} onChange={(e) => ponerFiltro(i, { op: e.target.value as any })}>
									<option value="contiene">Contiene</option><option value="es">Es Igual a</option>
								</select>
								<input className="av-buscar aj-valor" aria-label="Texto" maxLength={200} value={f.valor} placeholder="texto" onChange={(e) => ponerFiltro(i, { valor: e.target.value })} />
								<span className="aj-si">Entonces</span>
								<select className="av-buscar" aria-label="Acción" value={f.accion} onChange={(e) => ponerFiltro(i, { accion: e.target.value as any })}>
									<option value="mover">Mover a</option><option value="leido">Marcar como Leído</option><option value="destacar">Destacar</option><option value="eliminar">Eliminar</option>
								</select>
								{f.accion === "mover" ? (
									<>
										<input className="av-buscar" list="aj-carpetas" aria-label="Carpeta" placeholder="carpeta" maxLength={120} value={f.carpeta || ""} onChange={(e) => ponerFiltro(i, { carpeta: e.target.value })} />
										<label className="av-check"><input type="checkbox" checked={!!f.leido} onChange={(e) => ponerFiltro(i, { leido: e.target.checked })} /> Como Leído</label>
									</>
								) : null}
								<button className="av-link" onClick={() => setFiltros(filtros.filter((_, j) => j !== i))}>Quitar</button>
							</div>
						))}
						<datalist id="aj-carpetas">{lista.map((c) => <option key={c} value={c} />)}</datalist>
						<div className="aj-pie">
							<button className="av-btn" disabled={filtros.length >= 20} onClick={() => setFiltros([...filtros, { campo: "de", op: "contiene", valor: "", accion: "mover", carpeta: "Archive" }])}>Agregar Filtro</button>
							<button className="av-btn pri" disabled={ocupado || bloqueoSieve || sinSieve || filtros.some((f) => !f.valor.trim() || (f.accion === "mover" && !(f.carpeta || "").trim()))} onClick={guardarFiltros}>Guardar Cambios</button>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default AjustesCorreo;
